"use client";

import {
  GET_LINK_DRIVER,
  DELETE_LINKED_DRIVER,
  ADD_LINKED_DRIVER,
  ACCEPT_LINKED_DRIVER_REQUEST,
  SEND_MESSAGE_TO_LINKED_DRIVER,
} from "../end-points/link-driver";
import {
  getFetcher,
  postFetcher,
  // putFetcher,
  patchFetcher,
  deleteFetcher,
} from "../fetchers";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Toast } from "@/component/Shared/Toast";

// CONNECTED DRIVERS
const useAddLinkDriverMember = () => {
  const queryClient = useQueryClient();

  const { isPending, isError, isSuccess, mutate, data, error, reset } =
    useMutation({
      mutationKey: [ADD_LINKED_DRIVER],
      mutationFn: postFetcher,
      onSuccess: (data) => {
        Toast.success(data?.message);
        // Invalidate all queries that start with 'allLinkedDrivers'
        queryClient.invalidateQueries({
          queryKey: [GET_LINK_DRIVER],
          refetchType: "all",
        });
      },
      onError: (error) => {
        Toast.error(error?.message || "Failed to link driver");
      },
    });

  return {
    isError,
    isSuccess,
    isMutating: isPending,
    error: error,
    data: data,
    reset: reset,
    trigger: ({ memberId, message }: { memberId: string; message?: string }) =>
      mutate({
        url: ADD_LINKED_DRIVER,
        args: {
          memberId,
          ...(typeof message === "string" && message !== undefined
            ? { message }
            : {}),
        },
      }),
  };
};

const useGetAllLinkedDrivers = (
  // driverId: string,
  status?: string,
  paymentMethods?: string
) => {
  const { data, isLoading, isFetching, refetch, isSuccess, isError } = useQuery(
    {
      queryKey: [GET_LINK_DRIVER, status],
      queryFn: () =>
        getFetcher(
          `${GET_LINK_DRIVER}?${status ? `status=${status}` : ""}${
            paymentMethods ? `&paymentMethods=${true}` : ""
          }`
        ),
      // enabled: false, // Set this to false to disable this query from automatically running.
      enabled: !!status,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      retry: 0,
    }
  );

  return {
    data: data?.data || [],
    isLoading: isLoading || isFetching,
    isError,
    refetch,
    isSuccess,
  };
};

// this is auto called hook
const useGetAllLinkedDrivers2 = (
  // driverId: string,
  status?: string,
  paymentMethods?: string
) => {
  const { data, isLoading, isFetching, isSuccess, isError } = useQuery({
    queryKey: [GET_LINK_DRIVER, status],
    queryFn: () =>
      getFetcher(
        `${GET_LINK_DRIVER}?${status ? `status=${status}` : ""}${
          paymentMethods ? `&paymentMethods=${true}` : ""
        }`
      ),
    enabled: true, // Set this to false to disable this query from automatically running.
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  return {
    data: data?.data || [],
    isLoading: isLoading || isFetching,
    isError,
    isSuccess,
  };
};

const useAcceptDriverConnectionRequest = () => {
  const queryClient = useQueryClient();

  const { isPending, isError, isSuccess, mutate, data, error, reset } =
    useMutation({
      mutationKey: [ACCEPT_LINKED_DRIVER_REQUEST],
      mutationFn: patchFetcher,
      onSuccess: (data) => {
        Toast.success(data?.message);

        queryClient.invalidateQueries({
          queryKey: [GET_LINK_DRIVER],
          refetchType: "all",
        });
      },
      onError: (error: any) => {
        if (error?.message?.includes("expired")) {
          Toast.error(
            "This link is expired. The request has already been accepted."
          );
        } else {
          Toast.error(error?.message || "Failed to accept request");
        }
      },
    });

  return {
    isError,
    isSuccess,
    isMutating: isPending,
    error: error,
    data: data,
    reset: reset,
    trigger: (id: string) =>
      mutate({
        url: `${ACCEPT_LINKED_DRIVER_REQUEST}/${id}`,
      }),
  };
};

const useCancelDriverRequest = () => {
  const queryClient = useQueryClient();

  const { isPending, isError, isSuccess, mutate, data, error, reset } =
    useMutation({
      mutationKey: [DELETE_LINKED_DRIVER],
      mutationFn: (payloadData: any) =>
        deleteFetcher(
          `${DELETE_LINKED_DRIVER}/${payloadData?.linkedMemberId}?cancellerId=${payloadData?.cancellerId}`
        ),
      onSuccess: (data) => {
        Toast.success(data?.message);

        queryClient.invalidateQueries({
          queryKey: [GET_LINK_DRIVER],
          refetchType: "all",
        });
      },
      onError: (error: any) => {
        Toast.error(error?.message || "Failed to cancel request");
      },
    });

  return {
    isError,
    isSuccess,
    isMutating: isPending,
    error: error,
    data: data,
    reset: reset,
    trigger: (linkedMemberId: string, cancellerId: string) =>
      mutate({ linkedMemberId, cancellerId }),
  };
};

const useSendMessageToLinkedDriver = () => {
  const { isPending, isError, isSuccess, mutate, data, error, reset } =
    useMutation({
      mutationKey: [SEND_MESSAGE_TO_LINKED_DRIVER],
      mutationFn: postFetcher,
      onSuccess: (data) => {
        Toast.success(data?.message || "Message sent");
      },
      onError: (error: any) => {
        Toast.error(error?.message || "Failed to send message");
      },
    });

  return {
    isError,
    isSuccess,
    isMutating: isPending,
    error,
    data,
    reset,
    trigger: ({ memberId, message }: { memberId: string; message: string }) =>
      mutate({
        url: `${SEND_MESSAGE_TO_LINKED_DRIVER}/${memberId}`,
        args: { message },
      }),
  };
};

export {
  useAddLinkDriverMember,
  useGetAllLinkedDrivers,
  useAcceptDriverConnectionRequest,
  useCancelDriverRequest,
  useGetAllLinkedDrivers2,
  useSendMessageToLinkedDriver,
};
